import { useState } from "react";
import { toast } from "react-toastify";
import { X, RefreshCw, Copy, Eye, EyeOff } from "lucide-react";
import ModalPortal from "./ModalPortal";
import userService from "../services/userService";

// Generates an 8-character password guaranteed to contain at least one
// uppercase, lowercase, digit and symbol.
const generatePassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*?";
  const all = upper + lower + digits + symbols;

  const pick = (set) => set.charAt(Math.floor(Math.random() * set.length));

  const chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  while (chars.length < 8) chars.push(pick(all));

  // Shuffle so the guaranteed characters aren't always in the same position.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

// Browsers ignore autoComplete="off" for saved contacts and passwords. To stop
// the autofill dropdown, each input starts as read-only and becomes editable
// only when the user focuses it — the field can't be autofilled while read-only,
// but the user can still type normally. Spread `{...noAutofill}` onto an input.
const noAutofill = {
  readOnly: true,
  onFocus: (e) => e.currentTarget.removeAttribute("readonly"),
};

const AddUserDrawer = ({ open, onClose, onSuccess, facilityId }) => {
  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [markActive, setMarkActive] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Error returned by the API specifically for the email field (e.g. when the
  // email is already registered). Shown inline below the email input.
  const [emailServerError, setEmailServerError] = useState("");

  // --- Validation ---
  // Recomputed on every render. Errors are only displayed once `submitted` is
  // true, so messages appear after the first submit and then clear in
  // real-time as the user corrects each field.
  const getErrors = () => {
    const e = {};

    if (!firstName.trim()) e.firstName = "First name is required";
    else if (firstName.trim().length < 3)
      e.firstName = "First name must be at least 3 characters";

    if (!lastName.trim()) e.lastName = "Last name is required";
    else if (lastName.trim().length < 3)
      e.lastName = "Last name must be at least 3 characters";

    // A valid email looks like "name@domain.com": some text, an "@", more
    // text, a dot, then a couple more letters.
    if (!email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      e.email = "Enter a valid email address";

    if (phone.trim() && phone.replace(/\D/g, "").length !== 10)
      e.phone = "Phone number must be exactly 10 digits";

    if (department.trim() && department.trim().length < 2)
      e.department = "Department must be at least 2 characters";

    if (role.trim() && role.trim().length < 2)
      e.role = "Org role must be at least 2 characters";

    if (!password) e.password = "Password is required";
    else if (password.length < 8)
      e.password = "Password must be at least 8 characters";

    return e;
  };

  const errors = getErrors();

  // --- Password helpers ---
  const handleGeneratePassword = () => {
    setPassword(generatePassword());
    setShowPassword(true);
  };

  const handleCopyPassword = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Failed to copy password");
    }
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setSubmitted(true);
    setEmailServerError(""); // clear any previous server error before retrying

    if (Object.keys(getErrors()).length > 0) return;

    setIsSubmitting(true);
    try {
      // Required fields, matching the /users/add request body.
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        facility_id: facilityId ? Number(facilityId) : null,
        password,
        is_active: markActive,
        sendEmail,
      };

      // Optional fields — only sent when the user filled them in.
      if (phone.trim()) payload.phone_number = phone.trim();
      if (department.trim()) payload.org_department = department.trim();
      if (role.trim()) payload.org_role = role.trim();

      await userService.addUser(payload);

      toast.success("User added successfully");
      if (onSuccess) onSuccess();
      handleClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;

      // Keep the drawer open on any error so the user can fix and resubmit.
      if (detail === "Email already exists") {
        // Show this one inline under the email field instead of a toast.
        setEmailServerError("Email already registered");
      } else {
        toast.error(detail || "Something went wrong! Please try again");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Close / reset ---
  const handleClose = (refresh = false) => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setDepartment("");
    setRole("");
    setPassword("");
    setMarkActive(true);
    setSendEmail(false);
    setShowPassword(false);
    setSubmitted(false);
    setIsSubmitting(false);
    setEmailServerError("");
    onClose(refresh);
  };

  if (!open) return null;

  const inputClass =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 transition";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
  const errorClass = "text-xs text-red-500 mt-1.5 ml-1";

  return (
    <ModalPortal>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-40 transition-opacity"
        onClick={() => !isSubmitting && handleClose()}
      />

      {/* Drawer Panel */}
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Add User</h2>
          <button
            onClick={() => !isSubmitting && handleClose()}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-5">
            {/* First name & Last name (side by side) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  maxLength={20}
                  autoComplete="off"
                  {...noAutofill}
                  className={inputClass}
                />
                {submitted && errors.firstName && (
                  <p className={errorClass}>{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  maxLength={20}
                  autoComplete="off"
                  {...noAutofill}
                  className={inputClass}
                />
                {submitted && errors.lastName && (
                  <p className={errorClass}>{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className={labelClass}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailServerError(""); // clear server error as the user edits
                }}
                placeholder="Enter email address"
                autoComplete="off"
                {...noAutofill}
                className={inputClass}
              />
              {submitted && errors.email ? (
                <p className={errorClass}>{errors.email}</p>
              ) : emailServerError ? (
                <p className={errorClass}>{emailServerError}</p>
              ) : null}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="flex items-center gap-1 text-xs font-medium text-[#2B3674] hover:text-[#232d61] transition"
                >
                  <RefreshCw size={13} />
                  Generate
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter or generate a password"
                  autoComplete="new-password"
                  {...noAutofill}
                  className={`${inputClass} pr-20`}
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#2B3674] transition"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyPassword}
                    disabled={!password}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#2B3674] transition disabled:opacity-40 disabled:hover:bg-transparent"
                    title="Copy password"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              {submitted && errors.password && (
                <p className={errorClass}>{errors.password}</p>
              )}
            </div>

            {/* Phone number */}
            <div>
              <label className={labelClass}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                placeholder="10-digit phone number"
                autoComplete="off"
                {...noAutofill}
                className={inputClass}
              />
              {submitted && errors.phone && (
                <p className={errorClass}>{errors.phone}</p>
              )}
            </div>

            {/* Department (max 15 characters) */}
            <div>
              <label className={labelClass}>Department</label>
              <input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="Enter department"
                maxLength={15}
                autoComplete="off"
                {...noAutofill}
                className={inputClass}
              />
              {submitted && errors.department && (
                <p className={errorClass}>{errors.department}</p>
              )}
            </div>

            {/* Org role (max 15 characters) */}
            <div>
              <label className={labelClass}>Org Role</label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Enter org role"
                maxLength={15}
                autoComplete="off"
                {...noAutofill}
                className={inputClass}
              />
              {submitted && errors.role && (
                <p className={errorClass}>{errors.role}</p>
              )}
            </div>

            {/* Mark active */}
            <div className="flex items-center justify-between pt-1">
              <label className="text-sm font-medium text-gray-700">
                Mark Active
              </label>
              <button
                type="button"
                onClick={() => setMarkActive((a) => !a)}
                aria-pressed={markActive}
                className={`relative inline-flex items-center h-6 w-11 flex-shrink-0 px-1 cursor-pointer rounded-full transition-colors duration-200 ${
                  markActive ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    markActive ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Send credentials via email */}
            <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#2B3674] focus:ring-[#2B3674]"
              />
              <span className="text-sm font-medium text-gray-700">
                Send credentials via email
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => !isSubmitting && handleClose()}
            disabled={isSubmitting}
            className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium rounded-full bg-[#2B3674] text-white hover:bg-[#232d61] transition disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {isSubmitting ? "Adding..." : "Add User"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default AddUserDrawer;
